/**
 * @description
 * Service for fetching care pathway data for the currently authenticated patient.
 * Tries the FastAPI backend first; gracefully falls back to rich mock data
 * when the backend is unreachable (same pattern as backend-api.ts).
 */

import apiClient from '../api/api-client';
import { AxiosError } from 'axios';
import type { CarePathway } from '../types/care-pathway-types';

// --- Utility: detect network / backend errors ---

function isBackendUnavailable(err: unknown): boolean {
  if (err instanceof AxiosError) {
    return !err.response || (err.response.status >= 500 && err.response.status < 600);
  }
  return false;
}

// --- Mock fallback data ---

const MOCK_CARE_PATHWAYS: CarePathway[] = [
  {
    id: 'pathway-cardiac-001',
    name: 'Cardiac Care Pathway',
    description:
      'A structured program to evaluate and manage your cardiovascular health, from initial referral through ongoing monitoring.',
    status: 'active',
    startDate: '2026-01-10',
    estimatedEndDate: '2026-06-30',
    specialty: 'Cardiology',
    referringProvider: 'Dr. Sarah Chen',
    currentStepIndex: 2,
    steps: [
      {
        id: 'step-c1',
        title: 'GP Referral',
        description:
          'Your GP referred you to the cardiology team based on your recent check-up results.',
        status: 'completed',
        type: 'referral',
        completedDate: '2026-01-10',
        provider: 'Dr. Sarah Chen',
        location: 'KaiMed Primary Care, Level 2',
        notes: 'Referral sent electronically. Reason: elevated BP and family history.',
      },
      {
        id: 'step-c2',
        title: 'Cardiologist Consultation',
        description:
          'Initial consultation with the cardiology specialist to review your history and plan your care.',
        status: 'completed',
        type: 'consultation',
        completedDate: '2026-02-03',
        provider: 'Dr. James Okafor',
        location: 'KaiMed Heart Centre, Level 5',
        linkedAppointmentId: 'appt-001',
      },
      {
        id: 'step-c3',
        title: '12-Lead ECG',
        description: 'A resting electrocardiogram to record the electrical activity of your heart.',
        status: 'current',
        type: 'test',
        scheduledDate: '2026-03-20',
        provider: 'Cardiac Diagnostics Team',
        location: 'KaiMed Diagnostics, Ground Floor',
        linkedTestId: 'test-ecg-001',
      },
      {
        id: 'step-c4',
        title: 'Stress Echocardiogram',
        description:
          'An exercise stress test with ultrasound imaging to see how your heart performs under physical activity.',
        status: 'upcoming',
        type: 'imaging',
        scheduledDate: '2026-04-15',
        provider: 'Dr. James Okafor',
        location: 'KaiMed Heart Centre, Level 5',
      },
      {
        id: 'step-c5',
        title: 'Cardiology Follow-Up',
        description:
          'Review your test results with Dr. Okafor and discuss the next steps in your care plan.',
        status: 'upcoming',
        type: 'follow-up',
        scheduledDate: '2026-05-08',
        provider: 'Dr. James Okafor',
        location: 'KaiMed Heart Centre, Level 5',
      },
    ],
  },
  {
    id: 'pathway-diabetes-001',
    name: 'Diabetes Management Program',
    description:
      'A personalised plan to help you manage Type 2 diabetes through lifestyle changes, medication, and regular monitoring.',
    status: 'active',
    startDate: '2025-09-01',
    estimatedEndDate: '2026-09-01',
    specialty: 'Endocrinology',
    referringProvider: 'Dr. Priya Nair',
    currentStepIndex: 3,
    steps: [
      {
        id: 'step-d1',
        title: 'Diabetes Diagnosis & Education',
        description:
          'Initial diagnosis confirmed and diabetes education session completed with your care team.',
        status: 'completed',
        type: 'consultation',
        completedDate: '2025-09-05',
        provider: 'Dr. Priya Nair',
        location: 'KaiMed Primary Care, Level 2',
        notes: 'Patient completed 60-minute diabetes education module. HbA1c: 8.2%.',
      },
      {
        id: 'step-d2',
        title: 'Endocrinologist Assessment',
        description:
          'Specialist review with the endocrinology team to develop your personalised management plan.',
        status: 'completed',
        type: 'consultation',
        completedDate: '2025-10-12',
        provider: 'Dr. Maria Santos',
        location: 'KaiMed Specialist Clinics, Level 3',
        linkedAppointmentId: 'appt-002',
      },
      {
        id: 'step-d3',
        title: 'HbA1c Blood Test',
        description:
          'A blood test measuring your average blood sugar levels over the past 3 months.',
        status: 'completed',
        type: 'test',
        completedDate: '2026-01-08',
        provider: 'KaiMed Pathology',
        location: 'KaiMed Collection Centre, Ground Floor',
        linkedTestId: 'test-hba1c-002',
        notes: 'Result: 7.4% — improved from baseline.',
      },
      {
        id: 'step-d4',
        title: 'Medication Review & Adjustment',
        description:
          'Review your current Metformin dosage and consider additional medication if targets are not met.',
        status: 'current',
        type: 'medication',
        scheduledDate: '2026-03-25',
        provider: 'Dr. Maria Santos',
        location: 'KaiMed Specialist Clinics, Level 3',
        linkedAppointmentId: 'appt-003',
      },
      {
        id: 'step-d5',
        title: 'Quarterly Follow-Up',
        description:
          'Regular 3-monthly check-in to monitor your progress, review labs, and adjust your plan as needed.',
        status: 'upcoming',
        type: 'follow-up',
        scheduledDate: '2026-06-20',
        provider: 'Dr. Maria Santos',
        location: 'KaiMed Specialist Clinics, Level 3',
      },
    ],
  },
];

// --- Public API ---

/**
 * Fetches care pathways for the given patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getCarePathways(patientId: string): Promise<CarePathway[]> {
  try {
    const response = await apiClient.get<CarePathway[]>('/v1/care-pathways', {
      params: { patient_id: patientId },
    });
    return response.data;
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[care-pathway-service] getCarePathways: backend unavailable, using mock data.');
      return MOCK_CARE_PATHWAYS;
    }
    throw err;
  }
}
